'use client';

import {
  useId,
  useState,
  type ButtonHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { NavLink } from 'react-router';
import { GraduationCap, MapPin, UserRound } from 'lucide-react';
import { motion, useReducedMotion } from 'motion/react';
import { FadeIn, ModalMotion } from './uiMotion';
import lockIcon from './lock.svg';
import styles from './kit.module.scss';

type BtnProps = ButtonHTMLAttributes<HTMLButtonElement> & { children: ReactNode };

export function PrimaryButton({ className = '', children, ...props }: BtnProps) {
  return (
    <button type="button" className={`${styles.btn} ${styles.primary} ${className}`} {...props}>
      {children}
    </button>
  );
}

export function SecondaryButton({ className = '', children, ...props }: BtnProps) {
  return (
    <button type="button" className={`${styles.btn} ${styles.secondary} ${className}`} {...props}>
      {children}
    </button>
  );
}

export function GhostButton({ className = '', children, ...props }: BtnProps) {
  return (
    <button type="button" className={`${styles.btn} ${styles.ghost} ${className}`} {...props}>
      {children}
    </button>
  );
}

export function Field({
  label,
  hint,
  invalid,
  id,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  hint?: string;
  invalid?: boolean;
}) {
  const generated = useId();
  const inputId = id || generated;
  const hintId = `${inputId}-hint`;
  return (
    <label className={styles.field} htmlFor={inputId}>
      <span className={styles.label}>{label}</span>
      <input
        id={inputId}
        className={`${styles.control} ${invalid ? styles.invalid : ''}`}
        aria-invalid={invalid || undefined}
        aria-describedby={invalid && hint ? hintId : undefined}
        {...props}
      />
      {invalid && hint ? (
        <span id={hintId} className={styles.hintError}>
          {hint}
        </span>
      ) : null}
    </label>
  );
}

export function MarketingCard({
  title,
  text,
  action,
}: {
  title: string;
  text: string;
  action?: ReactNode;
}) {
  return (
    <article className={styles.cardMarketing}>
      <h3 className={styles.cardTitle}>{title}</h3>
      <p className={styles.cardText}>{text}</p>
      {action}
    </article>
  );
}

export function CabinetCard({
  title,
  text,
  action,
}: {
  title: string;
  text: string;
  action?: ReactNode;
}) {
  return (
    <article className={styles.cardCabinet}>
      <h3 className={styles.cardTitle}>{title}</h3>
      <p className={styles.cardText}>{text}</p>
      {action}
    </article>
  );
}

export function RequestCard({
  eyebrow,
  title,
  subtitle,
  meta,
  note,
  action,
  onOpen,
  art,
}: {
  eyebrow?: ReactNode;
  title: string;
  subtitle?: string;
  meta?: string[];
  note?: string;
  action?: ReactNode;
  onOpen?: () => void;
  /** r6: декоративный фон справа снизу (например GoalIllustration). */
  art?: ReactNode;
}) {
  return (
    <article className={art ? `${styles.request} ${styles.withArt}` : styles.request}>
      {art ? (
        <div className={styles.cardArt} aria-hidden="true">
          {art}
        </div>
      ) : null}
      {eyebrow ? <p className={styles.requestEyebrow}>{eyebrow}</p> : null}
      {onOpen ? (
        <button type="button" className={styles.requestOpen} onClick={onOpen}>
          {title}
        </button>
      ) : (
        <h3 className={styles.requestTitle}>{title}</h3>
      )}
      {subtitle ? <p className={styles.requestSub}>{subtitle}</p> : null}
      {meta && meta.length > 0 ? (
        <div className={styles.requestMeta}>
          {meta.map((line) => {
            /* design-fix 2026-10-09: параметры задачи чипами с иконкой (как в анкете) */
            const Icon = /Репетитор|Пробное/.test(line)
              ? GraduationCap
              : /Онлайн|У меня|У репетитора|Очно/.test(line)
                ? MapPin
                : UserRound;
            return (
              <p key={line} className={styles.requestChip}>
                <Icon size={16} aria-hidden />
                <span>{line}</span>
              </p>
            );
          })}
        </div>
      ) : null}
      {note ? (
        <p className={styles.requestNote} title={note}>
          {note}
        </p>
      ) : null}
      {action}
    </article>
  );
}

export function CabinetButton({ className = '', children, ...props }: BtnProps) {
  return (
    <PrimaryButton className={`${styles.inkAction} ${className}`} {...props}>
      {children}
    </PrimaryButton>
  );
}

export function RadioOption({
  name,
  label,
  checked,
  onChange,
}: {
  name: string;
  label: string;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <label className={styles.choice}>
      <input className={styles.hiddenInput} type="radio" name={name} checked={checked} onChange={onChange} />
      <span className={`${styles.box} ${styles.radio}`} aria-hidden="true">
        {checked ? <span className={styles.radioMark} /> : null}
      </span>
      {label}
    </label>
  );
}

export function ConsentCheckbox({
  checked,
  onChange,
  children,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  children: ReactNode;
}) {
  return (
    <label className={styles.choice}>
      <input
        className={styles.hiddenInput}
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className={`${styles.box} ${styles.check} ${checked ? styles.checkOn : ''}`} aria-hidden="true">
        {checked ? '✓' : ''}
      </span>
      {children}
    </label>
  );
}

export function CodeCell({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <input
      className={styles.code}
      inputMode="numeric"
      autoComplete="one-time-code"
      maxLength={1}
      value={value}
      aria-label="Цифра кода"
      onChange={(event) => onChange(event.target.value.replace(/\D/g, '').slice(-1))}
    />
  );
}

export function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  const reduce = useReducedMotion();
  return (
    <div className={styles.faq}>
      <button type="button" className={styles.faqQ} aria-expanded={open} onClick={() => setOpen((value) => !value)}>
        <span>{question}</span>
        <span aria-hidden="true">{open ? '-' : '+'}</span>
      </button>
      {open ? (
        <motion.p
          className={styles.faqA}
          initial={reduce ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
        >
          {answer}
        </motion.p>
      ) : null}
    </div>
  );
}

export function PriceRow({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.price}>
      <span>{label}</span>
      <span className={styles.priceValue}>{value}</span>
    </div>
  );
}

export function ProBadge({ tone }: { tone: 'red' | 'blue' | 'peach' }) {
  const toneClass = tone === 'red' ? styles.badgeRed : tone === 'blue' ? styles.badgeBlue : styles.badgePeach;
  return <span className={`${styles.badge} ${toneClass}`}>PRO</span>;
}

export function MenuItem({
  label,
  active,
  locked,
  hint,
  onClick,
  to,
  end,
  icon,
  secondary,
}: {
  label: string;
  active?: boolean;
  locked?: boolean;
  hint?: string;
  onClick?: () => void;
  to?: string;
  end?: boolean;
  /** Figma 3:30077: иконка 18px слева (единый набор lucide). */
  icon?: ReactNode;
  /** Нижняя группа меню (вопросы, поддержка, выход) - простой текст 14px. */
  secondary?: boolean;
}) {
  const iconNode = icon ? (
    <span className={styles.menuIcon} aria-hidden="true">
      {icon}
    </span>
  ) : null;
  if (locked) {
    return (
      <div className={`${styles.menuLocked} ${icon ? styles.menuLockedIcon : ''}`} aria-disabled="true" title={hint}>
        {iconNode ?? <img className={styles.menuLock} src={lockIcon} alt="" width={20} height={22} />}
        <span className={styles.menuCopy}>
          <span className={styles.menuName}>{label}</span>
          {hint ? <span className={styles.menuHint}>{hint}</span> : null}
        </span>
      </div>
    );
  }
  const base = `${styles.menu} ${secondary ? styles.menuSecondary : ''}`;
  const className = `${base} ${active ? styles.menuOn : ''}`;
  if (to) {
    return (
      <NavLink
        to={to}
        end={end}
        className={({ isActive }) => `${base} ${active || isActive ? styles.menuOn : ''}`}
        onClick={onClick}
      >
        {iconNode}
        {label}
      </NavLink>
    );
  }
  return (
    <button type="button" className={className} onClick={onClick}>
      {iconNode}
      {label}
    </button>
  );
}

export function ModalFrame({
  title,
  text,
  onClose,
  children,
}: {
  title: string;
  text: string;
  onClose: () => void;
  children?: ReactNode;
}) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={styles.backdrop}
      role="presentation"
      initial={reduce ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      onClick={onClose}
    >
      <ModalMotion>
        <div
          className={styles.modal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="kit-modal-title"
          onClick={(event) => event.stopPropagation()}
        >
          <h2 id="kit-modal-title" className={styles.modalTitle}>{title}</h2>
          <p className={styles.cardText}>{text}</p>
          <div className={styles.modalActions}>{children}</div>
        </div>
      </ModalMotion>
    </motion.div>
  );
}

export function StatusChip({
  label,
  tone = 'peach',
}: {
  label: string;
  tone?: 'peach' | 'blue';
}) {
  return (
    <span className={`${styles.chip} ${tone === 'blue' ? styles.chipBlue : ''}`}>
      <span className={styles.chipDot} aria-hidden="true" />
      {label}
    </span>
  );
}

export function EmptyState({
  title,
  fact,
  action,
  hint,
  children,
}: {
  title: string;
  fact: string;
  /** r3: необязательно - пустое состояние может быть только текстом, если кнопка уже есть в шапке. */
  action?: ReactNode;
  hint?: string;
  children?: ReactNode;
}) {
  return (
    <div className={styles.empty}>
      {children}
      <h2 className={styles.emptyTitle}>{title}</h2>
      <p className={styles.emptyFact}>{fact}</p>
      {action}
      {hint ? <p className={styles.emptyHint}>{hint}</p> : null}
    </div>
  );
}

export function SelectField({
  label,
  children,
  id,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement> & { label: string }) {
  const generated = useId();
  const inputId = id || generated;
  return (
    <label className={styles.field} htmlFor={inputId}>
      <span className={styles.label}>{label}</span>
      <select id={inputId} className={styles.control} {...props}>
        {children}
      </select>
    </label>
  );
}

export function BrandLockup({
  pro = 'red',
}: {
  pro?: 'red' | 'blue' | 'peach';
}) {
  const proClass = pro === 'peach' ? styles.proPeach : pro === 'blue' ? styles.proBlue : '';
  return (
    <div className={styles.brand}>
      <img className={styles.mark} src="/logo_red_main.svg" alt="" />
      <span className={styles.word}>MYTEACHER</span>
      <span className={`${styles.pro} ${proClass}`}>PRO</span>
    </div>
  );
}

export function HeaderBar({
  kind,
  nav,
  actions,
}: {
  kind: 'marketing' | 'cabinet' | 'guest';
  nav?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className={`${styles.headerBar} ${kind === 'marketing' ? '' : styles.headerCabinet}`}>
      <BrandLockup pro={kind === 'cabinet' ? 'peach' : 'red'} />
      {kind === 'marketing' ? <nav className={styles.navPill}>{nav}</nav> : null}
      {kind === 'guest' ? <span className={styles.guestNote}>Личный кабинет</span> : null}
      {kind !== 'guest' ? <div className={styles.headerActions}>{actions}</div> : null}
      {kind === 'marketing' ? (
        <button type="button" className={styles.burger} aria-label="Открыть меню">
          <span className={styles.burgerBars} />
        </button>
      ) : null}
    </header>
  );
}

export { FadeIn };
